/**
 * Per-person memory of the Orders page status filter (kanban 02f7bacc,
 * usage-graph finding C3).
 *
 * Emily picks the Draft tab as her first action on 30% of her visits;
 * Roblyn on 2% of hers. One global default would be wrong for one of them,
 * so the last filter each person chose is kept per user and reapplied when
 * the page opens without an explicit filter in the URL.
 *
 * Precedence when the page mounts (highest first):
 *   1. `?filter=<value>` in the URL: a shared deep link means exactly that.
 *   2. `?orderId=<uuid>` in the URL: an order deep link opens that order on
 *      the full list, so the row is there when the modal closes.
 *   3. The remembered filter for the signed-in user.
 *   4. `all`.
 *
 * Storage is the shared per-user localStorage memory (perUserMemory.ts):
 * keyed by user id, every read and write wrapped, so a blocked store degrades
 * to "nothing remembered", never to a crash.
 */

import { createPerUserMemory } from './perUserMemory';

export type OrderFilter =
  | 'all'
  | 'draft'
  | 'submission_failed'
  | 'submitted_to_sage'
  | 'confirmed_in_sage'
  | 'accepted'
  | 'shipped'
  | 'order_completed'
  | 'active'
  | 'on_hold';

export const ORDER_FILTER_VALUES: OrderFilter[] = [
  'all',
  'draft',
  'submission_failed',
  'submitted_to_sage',
  'confirmed_in_sage',
  'accepted',
  'shipped',
  'order_completed',
  'active',
  'on_hold',
];

export function isOrderFilter(v: string | null | undefined): v is OrderFilter {
  return typeof v === 'string' && (ORDER_FILTER_VALUES as string[]).includes(v);
}

/**
 * Why the page opened on the filter it did. Carried on the
 * `order_status_filter` portal event as `source` so the usage graph can tell
 * a restored filter from one the person clicked.
 */
export type OrderFilterSource = 'url' | 'order_link' | 'restored' | 'default';

export interface InitialOrderFilter {
  filter: OrderFilter;
  source: OrderFilterSource;
}

const memory = createPerUserMemory<OrderFilter>({
  storagePrefix: 'cm_portal_orders_filter:',
  isValid: isOrderFilter,
});

export function orderFilterStorageKey(userId: string): string {
  return memory.storageKey(userId);
}

/** The filter this user last chose on this browser, or null if none / invalid. */
export function readRememberedOrderFilter(userId: string | null | undefined): OrderFilter | null {
  return memory.read(userId);
}

/** Remember the filter this user just chose. `all` is remembered too: it is a choice. */
export function rememberOrderFilter(userId: string | null | undefined, filter: OrderFilter): void {
  memory.remember(userId, filter);
}

/**
 * Pure precedence rule for the filter the page opens on. See the module
 * comment for the order. `remembered` is whatever `readRememberedOrderFilter`
 * returned so the rule can be tested without a DOM.
 */
export function resolveInitialOrderFilter(input: {
  urlFilter: string | null;
  urlOrderId: string | null;
  remembered: OrderFilter | null;
}): InitialOrderFilter {
  if (isOrderFilter(input.urlFilter)) {
    return { filter: input.urlFilter, source: 'url' };
  }
  if (input.urlOrderId) {
    return { filter: 'all', source: 'order_link' };
  }
  if (input.remembered && input.remembered !== 'all') {
    return { filter: input.remembered, source: 'restored' };
  }
  return { filter: 'all', source: 'default' };
}
